import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, FileText, Mail, Phone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO
        title="Privacy Policy"
        description="Privacy policy for Energy Performance Direct. How we collect, use, and protect your personal data."
        canonical="/privacy"
        noindex={true}
      />
      <Navbar />

      {/* Header */}
      <section className="bg-gradient-to-r from-green-600 to-cyan-500 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="privacy-policy-heading">
              Privacy Policy
            </h1>
            <p className="text-lg opacity-95">UK GDPR Compliant</p>
            <p className="text-sm mt-2 opacity-90">Last updated: 20 November 2025</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="p-8">
                <div className="prose prose-green max-w-none">
                  <p className="text-lg text-gray-700 mb-6">
                    Energy Performance Direct ("we", "us", "our") is committed to protecting your personal information in accordance with UK GDPR, the Data Protection Act 2018, and all relevant UK privacy legislation.
                  </p>
                  <p className="text-gray-700 mb-8">
                    We collect and process personal data solely for delivering our services, fulfilling legal obligations, and supporting customers. We will never sell, rent, or trade your data.
                  </p>

                  {/* Section 1 */}
                  <div className="mb-10" id="who-we-are">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      1. Who We Are (Data Controller)
                    </h2>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <p className="font-semibold text-gray-900 mb-2">Energy Performance Direct Ltd</p>
                      <p className="text-gray-700">Company No: 08296332</p>
                      <p className="text-gray-700 mt-3 font-semibold">Registered Office:</p>
                      <p className="text-gray-700">10 Manston Terrace<br />Exeter<br />EX2 4NP<br />United Kingdom</p>
                      <p className="text-gray-700 mt-3 font-semibold">Contact:</p>
                      <p className="text-gray-700">
                        Email: <a href="mailto:info@energyperformancedirect.co.uk" className="text-green-600 hover:underline">info@energyperformancedirect.co.uk</a><br />
                        Telephone: <a href="tel:01392980077" className="text-green-600 hover:underline">01392 980077</a>
                      </p>
                      <p className="text-gray-700 mt-3 font-semibold">Data Protection Contact:</p>
                      <p className="text-gray-700">
                        Zak Henning – Managing Director<br />
                        Email: <a href="mailto:zak@energyperformancedirect.co.uk" className="text-green-600 hover:underline">zak@energyperformancedirect.co.uk</a>
                      </p>
                    </div>
                  </div>

                  {/* Section 2 */}
                  <div className="mb-10" id="lawful-bases">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      2. Lawful Bases for Processing
                    </h2>
                    <p className="text-gray-700 mb-4">We process personal data under:</p>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">2.1 Contract</h3>
                        <p className="text-gray-700">For delivering EPCs, energy surveys, calculations, reports, and booking confirmations.</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">2.2 Legitimate Interest</h3>
                        <p className="text-gray-700">For customer support, record keeping, analytics, fraud prevention, and review requests.</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">2.3 Legal Obligation</h3>
                        <p className="text-gray-700">For EPC lodgements and HMRC financial retention requirements.</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">2.4 Consent</h3>
                        <p className="text-gray-700">For newsletters and cookie-based analytics.</p>
                      </div>
                    </div>
                  </div>

                  {/* Section 3 */}
                  <div className="mb-10" id="data-collected">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      3. What Personal Data We Collect
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">3.1 Service Delivery & Bookings</h3>
                        <ul className="list-disc pl-6 text-gray-700">
                          <li>Name</li>
                          <li>Email</li>
                          <li>Telephone</li>
                          <li>Property address</li>
                          <li>Property details (rooms, heating type, measurements, photos)</li>
                          <li>Payment preferences (BACS, PayPal, cash, cheque)</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">3.2 EPC Lodgement Requirements</h3>
                        <p className="text-gray-700">Shared with Elmhurst or Quidos, as legally required.</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">3.3 Contact Forms / Emails</h3>
                        <p className="text-gray-700">Any personal information supplied in your enquiry.</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">3.4 Analytics & Cookies</h3>
                        <ul className="list-disc pl-6 text-gray-700">
                          <li>IP address (masked where possible)</li>
                          <li>Browser and device details</li>
                          <li>Pages viewed</li>
                          <li>Site interactions</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">3.5 Reviews</h3>
                        <p className="text-gray-700">Submitted through FreeIndex, Google, or Buy With Confidence.</p>
                      </div>
                    </div>
                  </div>

                  {/* Section 4 */}
                  <div className="mb-10" id="why-we-collect">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      4. Why We Collect Personal Data
                    </h2>
                    <p className="text-gray-700 mb-3">We collect and process your data to:</p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li>Process bookings</li>
                      <li>Provide energy assessments and EPCs</li>
                      <li>Deliver calculations and reports</li>
                      <li>Issue invoices and receipts</li>
                      <li>Comply with legal requirements</li>
                      <li>Improve website performance</li>
                      <li>Respond to enquiries</li>
                    </ul>
                    <p className="text-gray-700 mt-4 italic">No automated decision-making is used.</p>
                  </div>

                  {/* Section 5 */}
                  <div className="mb-10" id="data-retention">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      5. Data Retention
                    </h2>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <ul className="space-y-2 text-gray-700">
                        <li><strong>EPC data:</strong> 6 years</li>
                        <li><strong>Financial records:</strong> 6 years</li>
                        <li><strong>Enquiries:</strong> 12-24 months</li>
                        <li><strong>Analytics data:</strong> 12-26 months</li>
                        <li><strong>Emails:</strong> Up to 6 years</li>
                        <li><strong>Newsletter emails:</strong> Until unsubscribed</li>
                      </ul>
                    </div>
                  </div>

                  {/* Section 6 */}
                  <div className="mb-10" id="sharing-data">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      6. Sharing Your Data
                    </h2>
                    <p className="text-gray-700 mb-3">We share data only when necessary with:</p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li>Elmhurst Energy (EPC lodgement)</li>
                      <li>Quidos (EPC lodgement)</li>
                      <li>Nexcess (website hosting provider)</li>
                      <li>PayPal (payments)</li>
                      <li>Google Analytics (analytics)</li>
                      <li>Campaign Monitor (newsletter subscribers only)</li>
                    </ul>
                    <p className="text-gray-700 mt-4 font-semibold">We do not sell or trade data.</p>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mt-6">
                      <h3 className="font-semibold text-gray-900 mb-2">6.1 Payment processing &ndash; PayPal</h3>
                      <p className="text-gray-700 text-sm">
                        When you pay for a booking online we use <strong>PayPal (Europe) S.&agrave; r.l. et Cie, S.C.A.</strong> as our payment processor. If you choose to pay by card or PayPal balance, your full card number, CVV and PayPal account credentials are entered directly on PayPal&rsquo;s own secure checkout &mdash; we never see, store or process raw card numbers. PayPal returns to us only the transaction ID, amount, currency and order status. PayPal acts as an independent data controller for the card data it processes; their privacy notice is available at <a href="https://www.paypal.com/uk/legalhub/privacy-full" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">paypal.com/uk/legalhub/privacy-full</a>.
                      </p>
                      <p className="text-gray-700 text-sm mt-3">
                        If you choose to pay by BACS, cash or cheque after the survey, no card data is collected at booking time.
                      </p>
                    </div>
                  </div>

                  {/* Section 7 */}
                  <div className="mb-10" id="international-transfers">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      7. International Data Transfers
                    </h2>
                    <p className="text-gray-700 mb-3">Where data is transferred outside the UK, we use:</p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li>UK GDPR Standard Contractual Clauses (SCCs)</li>
                      <li>UK International Data Transfer Addendum (IDTA)</li>
                      <li>Appropriate safeguards</li>
                    </ul>
                  </div>

                  {/* Section 8 */}
                  <div className="mb-10" id="cookies">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      8. Cookies
                    </h2>
                    <p className="text-gray-700 mb-3">
                      We provide a cookie consent banner allowing you to accept, reject, or customise cookies.
                    </p>
                    <p className="text-gray-700">
                      Full details are available in our <Link to="/cookies" className="text-green-600 hover:underline font-semibold">Cookie Policy</Link>.
                    </p>
                  </div>

                  {/* Section 9 */}
                  <div className="mb-10" id="your-rights">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      9. Your Rights
                    </h2>
                    <p className="text-gray-700 mb-4">Under UK GDPR, you have the right to:</p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li>Access your data</li>
                      <li>Correct inaccuracies</li>
                      <li>Request deletion</li>
                      <li>Restrict processing</li>
                      <li>Data portability</li>
                      <li>Object to processing</li>
                      <li>Withdraw consent</li>
                    </ul>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <p className="text-gray-700">
                        <strong>To exercise your rights, contact:</strong><br />
                        Email: <a href="mailto:info@energyperformancedirect.co.uk" className="text-green-600 hover:underline">info@energyperformancedirect.co.uk</a>
                      </p>
                      <p className="text-gray-700 mt-3">
                        <strong>To lodge a complaint with the ICO:</strong><br />
                        <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">https://ico.org.uk</a>
                      </p>
                    </div>
                  </div>

                  {/* Section 10 */}
                  <div className="mb-10" id="data-security">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      10. Data Security
                    </h2>
                    <p className="text-gray-700 mb-4">
                      We use HTTPS encryption, secure UK hosting via Nexcess, and industry-standard security measures.
                    </p>
                    <p className="text-gray-700">
                      Any applicable breach will be reported to the ICO within 72 hours.
                    </p>
                  </div>

                  {/* Section 11 */}
                  <div className="mb-6" id="changes">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      11. Changes to This Policy
                    </h2>
                    <p className="text-gray-700">
                      We may update this policy. A new revision date will appear at the top of this page.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Navigation */}
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Navigation</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <a href="#who-we-are" className="text-green-600 hover:underline">1. Who We Are</a>
                  <a href="#lawful-bases" className="text-green-600 hover:underline">2. Lawful Bases</a>
                  <a href="#data-collected" className="text-green-600 hover:underline">3. Data Collected</a>
                  <a href="#why-we-collect" className="text-green-600 hover:underline">4. Why We Collect</a>
                  <a href="#data-retention" className="text-green-600 hover:underline">5. Data Retention</a>
                  <a href="#sharing-data" className="text-green-600 hover:underline">6. Sharing Data</a>
                  <a href="#international-transfers" className="text-green-600 hover:underline">7. Transfers</a>
                  <a href="#cookies" className="text-green-600 hover:underline">8. Cookies</a>
                  <a href="#your-rights" className="text-green-600 hover:underline">9. Your Rights</a>
                  <a href="#data-security" className="text-green-600 hover:underline">10. Security</a>
                  <a href="#changes" className="text-green-600 hover:underline">11. Changes</a>
                </div>
              </CardContent>
            </Card>

            {/* Related Links */}
            <div className="flex gap-4 justify-center">
              <Link to="/terms" className="text-green-600 hover:underline font-medium">
                Terms & Conditions
              </Link>
              <Link to="/cookies" className="text-green-600 hover:underline font-medium">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
